<h2 mat-dialog-title>
  @switch (currentStep) {
    @case ('file-select') {
      <span>Import Configuration</span>
    }
    @case ('preview') {
      <span>Review Changes</span>
    }
    @case ('importing') {
      <span>Importing...</span>
    }
    @case ('complete') {
      <span>Import Complete</span>
    }
    @case ('error') {
      <span>Import Error</span>
    }
  }
</h2>

<mat-dialog-content>
  <!-- Step 1: File Selection -->
  @if (currentStep === 'file-select') {
    <div class="step-content">
      <p class="step-description">
        Select a configuration ZIP file to import. This will preview the changes before applying them.
      </p>
      <div class="file-input-wrapper">
        <input type="file"
          #fileInput
          accept=".zip"
          (change)="onFileSelected($event)"
          style="display: none;">
          <button matButton="filled"
            color="primary"
            type="button"
            (click)="fileInput.click()"
            class="file-select-button">
            <mat-icon>upload_file</mat-icon>
            {{ selectedFile ? 'Change File' : 'Select ZIP File' }}
          </button>
          @if (selectedFile) {
            <div class="selected-file">
              <mat-icon>description</mat-icon>
              <span>{{ selectedFile.name }}</span>
              <span class="file-size">({{ (selectedFile.size / 1024).toFixed(1) }} KB)</span>
            </div>
          }
        </div>
        @if (errorMessage) {
          <div class="error-message">
            <mat-icon>error</mat-icon>
            <span>{{ errorMessage }}</span>
          </div>
        }
      </div>
    }

    <!-- Step 2: Preview Changes -->
    @if (currentStep === 'preview' && computedPreview) {
      <div class="step-content preview-content">
        <!-- Integrity Warnings -->
        @if (previewResult?.warnings?.length) {
          <div class="integrity-warnings">
            @for (warning of previewResult.warnings; track warning) {
              <div class="integrity-warning">
                <mat-icon>warning</mat-icon>
                <span>{{ warning }}</span>
              </div>
            }
          </div>
        }
        <div class="preview-header">
          <div class="tenant-info">
            <span class="label">Source:</span>
            <span class="value">{{ computedPreview.sourceTenantName }}</span>
            <span class="environment-name">({{ computedPreview.sourceEnvironmentName || 'Unknown' }})</span>
            <mat-icon>arrow_forward</mat-icon>
            <span class="label">Target:</span>
            <span class="value">{{ computedPreview.targetTenantName }}</span>
            <span class="environment-name">({{ computedPreview.targetEnvironmentName || 'Unknown' }})</span>
          </div>
          <div class="summary-badge" [class.no-changes]="hasNoChanges">
            {{ hasNoChanges ? 'No changes' : (totalChanges | plural: 'total change') }}
          </div>
        </div>
        <!-- No Changes Message -->
        @if (hasNoChanges) {
          <div class="no-changes-message">
            <mat-icon class="teller-online-icon--success">check_circle</mat-icon>
            <h3>No Changes Detected</h3>
            <p>The configuration in this ZIP file matches the current configuration. There is nothing to import.</p>
          </div>
        }
        <!-- Site Properties -->
        @if (computedPreview.siteChanges?.length) {
          <div class="preview-section">
            <h4>Site Properties</h4>
            <div class="property-changes">
              @for (change of computedPreview.siteChanges; track change) {
                <div class="property-row">
                  <span class="property-name">{{ change.name }}:</span>
                  @if (change.hasChanged) {
                    <span class="property-current">{{ change.currentValue }}</span>
                    <mat-icon>arrow_forward</mat-icon>
                    <span class="property-incoming">{{ change.incomingValue }}</span>
                  } @else {
                    <span class="property-unchanged">{{ change.currentValue }}</span>
                    <span class="change-badge small change-unchanged">No changes</span>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- Teller Connections -->
        @if (computedPreview.tellerConnections?.length) {
          <div class="preview-section">
            <h4>Teller Connections</h4>
            <mat-accordion class="entity-accordion" multi>
              @for (connection of computedPreview.tellerConnections; track connection) {
                <mat-expansion-panel class="entity-panel">
                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                    <mat-panel-title>
                      <span class="change-badge" [ngClass]="getChangeTypeClass(connection.changeType)">
                        {{ getChangeTypeLabel(connection.changeType) }}
                      </span>
                      <span class="entity-name">{{ connection.data.name || connection.data.tellerSystemCode }}</span>
                    </mat-panel-title>
                    <mat-panel-description>
                      @if (connection.changeType === ChangeType.Create) {
                        <span class="change-count">{{ getTellerConnectionNewFieldCount(connection.data) | plural: 'new value' }}</span>
                      }
                      @if (connection.changeType !== ChangeType.Create && connection.propertyChanges?.length) {
                        <span class="change-count">
                          {{ connection.propertyChanges.length | plural: 'property change' }}
                        </span>
                      }
                    </mat-panel-description>
                  </mat-expansion-panel-header>
                  <!-- Teller Connection Property Changes -->
                  @if (connection.propertyChanges?.length) {
                    <div class="property-changes">
                      @for (change of connection.propertyChanges; track change) {
                        <div class="property-row">
                          <span class="property-name">{{ change.name }}:</span>
                          <span class="property-current">{{ change.currentValue }}</span>
                          <mat-icon>arrow_forward</mat-icon>
                          <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                      }
                    </div>
                  }
                  @if (!connection.propertyChanges?.length && connection.changeType === ChangeType.Create) {
                    <div class="new-entity-notice">
                      New teller connection will be created
                    </div>
                  }
                  @if (!connection.propertyChanges?.length && connection.changeType === ChangeType.Unchanged) {
                    <div class="no-changes-notice">
                      No changes to teller connection properties
                    </div>
                  }
                  @if (!connection.propertyChanges?.length && connection.changeType === ChangeType.Update) {
                    <div class="no-changes-notice">
                      No changes to teller connection properties (see Pages section for child changes)
                    </div>
                  }
                </mat-expansion-panel>
              }
            </mat-accordion>
          </div>
        }
        <!-- Pages -->
        @if (getTotalPageCount() > 0) {
          <div class="preview-section">
            <h4>Pages</h4>
            <mat-accordion class="entity-accordion" multi>
              @for (connection of computedPreview.tellerConnections; track connection) {
                @for (page of connection.pages; track page) {
                  <mat-expansion-panel class="entity-panel">
                    <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                      <mat-panel-title>
                        <span class="change-badge" [ngClass]="getChangeTypeClass(page.changeType)">
                          {{ getChangeTypeLabel(page.changeType) }}
                        </span>
                        <span class="entity-name">{{ page.data.header || '(No header)' }}</span>
                      </mat-panel-title>
                      <mat-panel-description>
                        <span class="entity-details">{{ connection.data.name || connection.data.tellerSystemCode }}</span>
                        @if (page.data.tellerBusinessSystemNo) {
                          <span class="entity-details">
                            BSN: {{ page.data.tellerBusinessSystemNo }}
                          </span>
                        }
                        @if (page.changeType === ChangeType.Create) {
                          <span class="change-count">
                            {{ getPageNewFieldCount(page.data) + getPageChildChangesCount(page) | plural: 'new value' }}
                          </span>
                        }
                        @if (page.changeType !== ChangeType.Create && (page.propertyChanges?.length || getPageChildChangesCount(page) > 0)) {
                          <span class="change-count">
                            {{ (page.propertyChanges?.length || 0) + getPageChildChangesCount(page) | plural: 'change' }}
                          </span>
                        }
                      </mat-panel-description>
                    </mat-expansion-panel-header>
                    <!-- Page Property Changes -->
                    @if (page.propertyChanges?.length) {
                      <div class="property-changes">
                        @for (change of page.propertyChanges; track change) {
                          <div class="property-row">
                            <span class="property-name">{{ change.name }}:</span>
                            <span class="property-current">{{ change.currentValue }}</span>
                            <mat-icon>arrow_forward</mat-icon>
                            <span class="property-incoming">{{ change.incomingValue }}</span>
                          </div>
                        }
                      </div>
                    }
                    @if (!page.propertyChanges?.length && page.changeType === ChangeType.Create) {
                      <div class="new-entity-notice">
                        New page will be created
                      </div>
                    }
                    @if (!page.propertyChanges?.length && page.changeType === ChangeType.Unchanged) {
                      <div class="no-changes-notice">
                        No changes to page properties
                      </div>
                    }
                    <!-- Child Entities Section -->
                    @if (hasPageChildEntities(page)) {
                      <div class="child-entities-section">
                        <h5 class="child-entities-header">Child Entities</h5>
                        <!-- SearchTypes -->
                        @if (page.searchTypes?.length) {
                          <div class="child-entity-group">
                            <mat-accordion class="child-accordion" multi>
                              @for (searchType of page.searchTypes; track searchType) {
                                <mat-expansion-panel class="child-entity-panel">
                                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                                    <mat-panel-title>
                                      <span class="change-badge small" [ngClass]="getChangeTypeClass(searchType.changeType)">
                                        {{ getChangeTypeLabel(searchType.changeType) }}
                                      </span>
                                      <mat-icon class="entity-type-icon">search</mat-icon>
                                      <span class="entity-name">{{ searchType.data.label }}</span>
                                    </mat-panel-title>
                                    <mat-panel-description>
                                      <span class="entity-type-label">Search Type</span>
                                      @if (searchType.changeType === ChangeType.Create) {
                                        <span class="change-count">{{ getSearchTypeNewFieldCount(searchType.data) | plural: 'new value' }}</span>
                                      }
                                      @if (searchType.changeType !== ChangeType.Create && searchType.propertyChanges?.length) {
                                        <span class="change-count">
                                          {{ searchType.propertyChanges.length | plural: 'change' }}
                                        </span>
                                      }
                                    </mat-panel-description>
                                  </mat-expansion-panel-header>
                                  @if (searchType.propertyChanges?.length) {
                                    <div class="property-changes">
                                      @for (change of searchType.propertyChanges; track change) {
                                        <div class="property-row">
                                          <span class="property-name">{{ change.name }}:</span>
                                          <span class="property-current">{{ change.currentValue }}</span>
                                          <mat-icon>arrow_forward</mat-icon>
                                          <span class="property-incoming">{{ change.incomingValue }}</span>
                                        </div>
                                      }
                                    </div>
                                  }
                                  <!-- Instruction Image -->
                                  @if (searchType.instructionImage && searchType.instructionImage.changeType !== AssetChangeType.None) {
                                    <div class="instruction-image-section">
                                      <span class="asset-badge" [ngClass]="{
                                        'asset-new': searchType.instructionImage.changeType === AssetChangeType.New,
                                        'asset-replace': searchType.instructionImage.changeType === AssetChangeType.Replace,
                                        'asset-unchanged': searchType.instructionImage.changeType === AssetChangeType.Unchanged
                                      }">
                                        @if (searchType.instructionImage.changeType === AssetChangeType.New) {
                                          <mat-icon>add_circle</mat-icon>
                                        }
                                        @if (searchType.instructionImage.changeType === AssetChangeType.Replace) {
                                          <mat-icon>sync</mat-icon>
                                        }
                                        @if (searchType.instructionImage.changeType === AssetChangeType.Unchanged) {
                                          <mat-icon>check_circle</mat-icon>
                                        }
                                        Instruction Image
                                      </span>
                                      <span class="asset-change-type">
                                        @switch (searchType.instructionImage.changeType) {
                                          @case (AssetChangeType.New) {
                                            New
                                          }
                                          @case (AssetChangeType.Replace) {
                                            Will replace existing
                                          }
                                          @case (AssetChangeType.Unchanged) {
                                            No changes
                                          }
                                        }
                                      </span>
                                    </div>
                                  }
                                  @if (!searchType.propertyChanges?.length && searchType.changeType === ChangeType.Create) {
                                    <div
                                      class="new-entity-notice">
                                      New search type will be created
                                    </div>
                                  }
                                  @if (!searchType.propertyChanges?.length && searchType.changeType === ChangeType.Unchanged) {
                                    <div
                                      class="no-changes-notice">
                                      No changes to search type properties
                                    </div>
                                  }
                                </mat-expansion-panel>
                              }
                            </mat-accordion>
                          </div>
                        }
                        <!-- PageItemTypes -->
                        @if (page.pageItemTypes?.length) {
                          <div class="child-entity-group">
                            <mat-accordion class="child-accordion" multi>
                              @for (itemType of page.pageItemTypes; track itemType) {
                                <mat-expansion-panel class="child-entity-panel">
                                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                                    <mat-panel-title>
                                      <span class="change-badge small" [ngClass]="getChangeTypeClass(itemType.changeType)">
                                        {{ getChangeTypeLabel(itemType.changeType) }}
                                      </span>
                                      <mat-icon class="entity-type-icon">inventory_2</mat-icon>
                                      <span class="entity-name">{{ itemType.data.label }}</span>
                                    </mat-panel-title>
                                    <mat-panel-description>
                                      <span class="entity-type-label">Page Item Type</span>
                                      @if (itemType.changeType === ChangeType.Create) {
                                        <span class="change-count">{{ getPageItemTypeNewFieldCount(itemType.data) | plural: 'new value' }}</span>
                                      }
                                      @if (itemType.changeType !== ChangeType.Create && itemType.propertyChanges?.length) {
                                        <span class="change-count">
                                          {{ itemType.propertyChanges.length | plural: 'change' }}
                                        </span>
                                      }
                                    </mat-panel-description>
                                  </mat-expansion-panel-header>
                                  @if (itemType.propertyChanges?.length) {
                                    <div class="property-changes">
                                      @for (change of itemType.propertyChanges; track change) {
                                        <div class="property-row">
                                          <span class="property-name">{{ change.name }}:</span>
                                          <span class="property-current">{{ change.currentValue }}</span>
                                          <mat-icon>arrow_forward</mat-icon>
                                          <span class="property-incoming">{{ change.incomingValue }}</span>
                                        </div>
                                      }
                                    </div>
                                  }
                                  <!-- Image -->
                                  @if (itemType.image && itemType.image.changeType !== AssetChangeType.None) {
                                    <div class="instruction-image-section">
                                      <span class="asset-badge" [ngClass]="{
                                        'asset-new': itemType.image.changeType === AssetChangeType.New,
                                        'asset-replace': itemType.image.changeType === AssetChangeType.Replace,
                                        'asset-unchanged': itemType.image.changeType === AssetChangeType.Unchanged
                                      }">
                                        @if (itemType.image.changeType === AssetChangeType.New) {
                                          <mat-icon>add_circle</mat-icon>
                                        }
                                        @if (itemType.image.changeType === AssetChangeType.Replace) {
                                          <mat-icon>sync</mat-icon>
                                        }
                                        @if (itemType.image.changeType === AssetChangeType.Unchanged) {
                                          <mat-icon>check_circle</mat-icon>
                                        }
                                        Image
                                      </span>
                                      <span class="asset-change-type">
                                        @switch (itemType.image.changeType) {
                                          @case (AssetChangeType.New) {
                                            New
                                          }
                                          @case (AssetChangeType.Replace) {
                                            Will replace existing
                                          }
                                          @case (AssetChangeType.Unchanged) {
                                            No changes
                                          }
                                        }
                                      </span>
                                    </div>
                                  }
                                  @if (!itemType.propertyChanges?.length && itemType.changeType === ChangeType.Create) {
                                    <div
                                      class="new-entity-notice">
                                      New page item type will be created
                                    </div>
                                  }
                                  @if (!itemType.propertyChanges?.length && itemType.changeType === ChangeType.Unchanged) {
                                    <div
                                      class="no-changes-notice">
                                      No changes to page item type properties
                                    </div>
                                  }
                                </mat-expansion-panel>
                              }
                            </mat-accordion>
                          </div>
                        }
                        <!-- InboundRedirectSources -->
                        @if (page.inboundRedirectSources?.length) {
                          <div class="child-entity-group">
                            <mat-accordion class="child-accordion" multi>
                              @for (redirect of page.inboundRedirectSources; track redirect) {
                                <mat-expansion-panel class="child-entity-panel">
                                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                                    <mat-panel-title>
                                      <span class="change-badge small" [ngClass]="getChangeTypeClass(redirect.changeType)">
                                        {{ getChangeTypeLabel(redirect.changeType) }}
                                      </span>
                                      <mat-icon class="entity-type-icon">input</mat-icon>
                                      <span class="entity-name">{{ redirect.data.label || 'Redirect ' + redirect.data.sourceKey }}</span>
                                    </mat-panel-title>
                                    <mat-panel-description>
                                      <span class="entity-type-label">Inbound Redirect</span>
                                      @if (redirect.changeType === ChangeType.Create) {
                                        <span class="change-count">{{ getInboundRedirectSourceNewFieldCount(redirect.data) | plural: 'new value' }}</span>
                                      }
                                      @if (redirect.changeType !== ChangeType.Create && redirect.propertyChanges?.length) {
                                        <span class="change-count">
                                          {{ redirect.propertyChanges.length | plural: 'change' }}
                                        </span>
                                      }
                                    </mat-panel-description>
                                  </mat-expansion-panel-header>
                                  @if (redirect.propertyChanges?.length) {
                                    <div class="property-changes">
                                      @for (change of redirect.propertyChanges; track change) {
                                        <div class="property-row">
                                          <span class="property-name">{{ change.name }}:</span>
                                          <span class="property-current">{{ change.currentValue }}</span>
                                          <mat-icon>arrow_forward</mat-icon>
                                          <span class="property-incoming">{{ change.incomingValue }}</span>
                                        </div>
                                      }
                                    </div>
                                  }
                                  @if (!redirect.propertyChanges?.length && redirect.changeType === ChangeType.Create) {
                                    <div
                                      class="new-entity-notice">
                                      New inbound redirect source will be created
                                    </div>
                                  }
                                  @if (!redirect.propertyChanges?.length && redirect.changeType === ChangeType.Unchanged) {
                                    <div
                                      class="no-changes-notice">
                                      No changes to redirect properties
                                    </div>
                                  }
                                </mat-expansion-panel>
                              }
                            </mat-accordion>
                          </div>
                        }
                      </div>
                    }
                  </mat-expansion-panel>
                }
              }
            </mat-accordion>
          </div>
        }
        <!-- Menu Items -->
        @if (computedPreview.menuItems?.length) {
          <div class="preview-section">
            <h4>Menu Items</h4>
            <mat-accordion class="entity-accordion" multi>
              @for (item of computedPreview.menuItems; track item) {
                <mat-expansion-panel class="entity-panel">
                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                    <mat-panel-title>
                      <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                        {{ getChangeTypeLabel(item.changeType) }}
                      </span>
                      <span class="entity-name">{{ item.data.label }}</span>
                    </mat-panel-title>
                    <mat-panel-description>
                      @if (item.changeType === ChangeType.Create) {
                        <span class="change-count">{{ getMenuItemNewFieldCount(item.data) | plural: 'new value' }}</span>
                      }
                      @if (item.changeType !== ChangeType.Create && item.propertyChanges?.length) {
                        <span class="change-count">
                          {{ item.propertyChanges.length | plural: 'change' }}
                        </span>
                      }
                    </mat-panel-description>
                  </mat-expansion-panel-header>
                  @if (item.propertyChanges?.length) {
                    <div class="property-changes">
                      @for (change of item.propertyChanges; track change) {
                        <div class="property-row">
                          <span class="property-name">{{ change.name }}:</span>
                          <span class="property-current">{{ change.currentValue }}</span>
                          <mat-icon>arrow_forward</mat-icon>
                          <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                      }
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Create) {
                    <div class="new-entity-notice">
                      New menu item will be created
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged) {
                    <div class="no-changes-notice">
                      No changes to menu item properties
                    </div>
                  }
                </mat-expansion-panel>
              }
            </mat-accordion>
          </div>
        }
        <!-- Email Templates -->
        @if (computedPreview.emailTemplates?.length) {
          <div class="preview-section">
            <h4>Email Templates</h4>
            <mat-accordion class="entity-accordion" multi>
              @for (item of computedPreview.emailTemplates; track item) {
                <mat-expansion-panel class="entity-panel">
                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                    <mat-panel-title>
                      <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                        {{ getChangeTypeLabel(item.changeType) }}
                      </span>
                      <span class="entity-name">{{ item.data.subject || getEmailTemplateName(item.data.name) }}</span>
                    </mat-panel-title>
                    <mat-panel-description>
                      @if (item.changeType === ChangeType.Create) {
                        <span class="change-count">{{ getEmailTemplateNewFieldCount(item.data) | plural: 'new value' }}</span>
                      }
                      @if (item.changeType !== ChangeType.Create && item.propertyChanges?.length) {
                        <span class="change-count">
                          {{ item.propertyChanges.length | plural: 'change' }}
                        </span>
                      }
                    </mat-panel-description>
                  </mat-expansion-panel-header>
                  @if (item.propertyChanges?.length) {
                    <div class="property-changes">
                      @for (change of item.propertyChanges; track change) {
                        <div class="property-row">
                          <span class="property-name">{{ change.name }}:</span>
                          <span class="property-current">{{ change.currentValue }}</span>
                          <mat-icon>arrow_forward</mat-icon>
                          <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                      }
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Create) {
                    <div class="new-entity-notice">
                      New email template will be created
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged) {
                    <div class="no-changes-notice">
                      No changes to email template
                    </div>
                  }
                </mat-expansion-panel>
              }
            </mat-accordion>
          </div>
        }
        <!-- SMS Templates -->
        @if (computedPreview.smsTemplates?.length) {
          <div class="preview-section">
            <h4>SMS Templates</h4>
            <mat-accordion class="entity-accordion" multi>
              @for (item of computedPreview.smsTemplates; track item) {
                <mat-expansion-panel class="entity-panel">
                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                    <mat-panel-title>
                      <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                        {{ getChangeTypeLabel(item.changeType) }}
                      </span>
                      <span class="entity-name">{{ getSmsTemplateName(item.data.name) }}</span>
                    </mat-panel-title>
                    <mat-panel-description>
                      @if (item.changeType === ChangeType.Create) {
                        <span class="change-count">{{ getSmsTemplateNewFieldCount(item.data) | plural: 'new value' }}</span>
                      }
                      @if (item.changeType !== ChangeType.Create && item.propertyChanges?.length) {
                        <span class="change-count">
                          {{ item.propertyChanges.length | plural: 'change' }}
                        </span>
                      }
                    </mat-panel-description>
                  </mat-expansion-panel-header>
                  @if (item.propertyChanges?.length) {
                    <div class="property-changes">
                      @for (change of item.propertyChanges; track change) {
                        <div class="property-row">
                          <span class="property-name">{{ change.name }}:</span>
                          <span class="property-current">{{ change.currentValue }}</span>
                          <mat-icon>arrow_forward</mat-icon>
                          <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                      }
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Create) {
                    <div class="new-entity-notice">
                      New SMS template will be created
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged) {
                    <div class="no-changes-notice">
                      No changes to SMS template
                    </div>
                  }
                </mat-expansion-panel>
              }
            </mat-accordion>
          </div>
        }
        <!-- Terminology -->
        @if (computedPreview.terminology?.length) {
          <div class="preview-section">
            <h4>Terminology</h4>
            <mat-accordion class="entity-accordion" multi>
              @for (item of computedPreview.terminology; track item) {
                <mat-expansion-panel class="entity-panel">
                  <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                    <mat-panel-title>
                      <span class="change-badge" [ngClass]="getChangeTypeClass(item.changeType)">
                        {{ getChangeTypeLabel(item.changeType) }}
                      </span>
                      <span class="entity-name">{{ getTerminologyName(item.data.name) }}</span>
                    </mat-panel-title>
                    <mat-panel-description>
                      @if (item.changeType === ChangeType.Create) {
                        <span class="change-count">{{ getTerminologyNewFieldCount(item.data) | plural: 'new value' }}</span>
                      }
                      @if (item.changeType !== ChangeType.Create && item.propertyChanges?.length) {
                        <span class="change-count">
                          {{ item.propertyChanges.length | plural: 'change' }}
                        </span>
                      }
                    </mat-panel-description>
                  </mat-expansion-panel-header>
                  @if (item.propertyChanges?.length) {
                    <div class="property-changes">
                      @for (change of item.propertyChanges; track change) {
                        <div class="property-row">
                          <span class="property-name">{{ change.name }}:</span>
                          <span class="property-current">{{ change.currentValue }}</span>
                          <mat-icon>arrow_forward</mat-icon>
                          <span class="property-incoming">{{ change.incomingValue }}</span>
                        </div>
                      }
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Create) {
                    <div class="new-entity-notice">
                      New terminology override will be created
                    </div>
                  }
                  @if (!item.propertyChanges?.length && item.changeType === ChangeType.Unchanged) {
                    <div class="no-changes-notice">
                      No changes to terminology override
                    </div>
                  }
                </mat-expansion-panel>
              }
            </mat-accordion>
          </div>
        }
        <!-- Assets -->
        @if (computedPreview.assetChanges?.length) {
          <div class="preview-section">
            <h4>Assets</h4>
            <div class="asset-list">
              @for (asset of computedPreview.assetChanges; track asset) {
                <div class="asset-item">
                  <span class="asset-badge" [ngClass]="{
                    'asset-new': asset.changeType === AssetChangeType.New,
                    'asset-replace': asset.changeType === AssetChangeType.Replace,
                    'asset-unchanged': asset.changeType === AssetChangeType.Unchanged
                  }">
                    @if (asset.changeType === AssetChangeType.New) {
                      <mat-icon>add_circle</mat-icon>
                    }
                    @if (asset.changeType === AssetChangeType.Replace) {
                      <mat-icon>sync</mat-icon>
                    }
                    @if (asset.changeType === AssetChangeType.Unchanged) {
                      <mat-icon>check_circle</mat-icon>
                    }
                    {{ asset.name }}
                  </span>
                  <span class="asset-change-type">
                    @switch (asset.changeType) {
                      @case (AssetChangeType.New) {
                        New
                      }
                      @case (AssetChangeType.Replace) {
                        Will replace existing
                      }
                      @case (AssetChangeType.Unchanged) {
                        No changes
                      }
                    }
                  </span>
                </div>
              }
            </div>
          </div>
        }
        @if (!hasNoChanges) {
          <div class="warning-message">
            <mat-icon>warning</mat-icon>
            <span>This will replace existing configuration data. This action cannot be undone.</span>
          </div>
        }
      </div>
    }

    <!-- Step 3: Importing -->
    @if (currentStep === 'importing') {
      <div class="step-content centered">
        <mat-spinner diameter="48"></mat-spinner>
        <p>Importing configuration...</p>
        <p class="sub-text">Please do not close this dialog.</p>
      </div>
    }

    <!-- Step 4: Complete -->
    @if (currentStep === 'complete') {
      <div class="step-content centered">
        <mat-icon class="success-icon teller-online-icon--success">check_circle</mat-icon>
        <h3>Import Successful</h3>
        <p>The configuration has been imported successfully.</p>
        @if (importResult?.summary) {
          <div class="import-summary">
            <div class="summary-row">
              <span>Total Changes:</span>
              <span>{{ importResult.summary.totalChanges }} ({{ importResult.summary.totalPropertyChanges }} properties, {{ importResult.summary.totalAssetChanges }} assets)</span>
            </div>
            @if (importResult.summary.entitiesWithChanges) {
              @if (importResult.summary.entitiesWithChanges['TellerConnections']) {
                <div class="summary-row">
                  <span>Teller Connections:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['TellerConnections'] }} with changes</span>
                </div>
              }
              @if (importResult.summary.entitiesWithChanges['Pages']) {
                <div class="summary-row">
                  <span>Pages:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['Pages'] }} with changes</span>
                </div>
              }
              @if (importResult.summary.entitiesWithChanges['MenuItems']) {
                <div class="summary-row">
                  <span>Menu Items:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['MenuItems'] }} with changes</span>
                </div>
              }
              @if (importResult.summary.entitiesWithChanges['EmailTemplates']) {
                <div class="summary-row">
                  <span>Email Templates:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['EmailTemplates'] }} with changes</span>
                </div>
              }
              @if (importResult.summary.entitiesWithChanges['SMSTemplates']) {
                <div class="summary-row">
                  <span>SMS Templates:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['SMSTemplates'] }} with changes</span>
                </div>
              }
              @if (importResult.summary.entitiesWithChanges['Terminology']) {
                <div class="summary-row">
                  <span>Terminology:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['Terminology'] }} with changes</span>
                </div>
              }
              @if (importResult.summary.entitiesWithChanges['Assets']) {
                <div class="summary-row">
                  <span>Assets:</span>
                  <span>{{ importResult.summary.entitiesWithChanges['Assets'] }} changed</span>
                </div>
              }
            }
          </div>
        }
      </div>
    }

    <!-- Error State -->
    @if (currentStep === 'error') {
      <div class="step-content centered">
        <mat-icon class="error-icon">error</mat-icon>
        <h3>Import Failed</h3>
        <div class="error-details">
          <pre>{{ errorMessage }}</pre>
        </div>
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    @switch (currentStep) {
      <!-- File Selection Actions -->
      @case ('file-select') {
        <button matButton mat-dialog-close>Cancel</button>
        <button matButton="filled"
          color="primary"
          [disabled]="!selectedFile || isLoading"
          (click)="onPreview()">
          @if (isLoading) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          @if (!isLoading) {
            <span>Preview Changes</span>
          }
        </button>
      }
      <!-- Preview Actions -->
      @case ('preview') {
        <button matButton (click)="onBack()">Back</button>
        <button matButton mat-dialog-close>{{ hasNoChanges ? 'Close' : 'Cancel' }}</button>
        @if (!hasNoChanges) {
          <button matButton="filled"
            color="warn"
            (click)="onConfirmImport()">
            Confirm Import
          </button>
        }
      }
      <!-- Importing Actions -->
      @case ('importing') {
        <!-- No actions while importing -->
      }
      <!-- Complete Actions -->
      @case ('complete') {
        <button matButton="filled" color="primary" (click)="onComplete()">Done</button>
      }
      <!-- Error Actions -->
      @case ('error') {
        <button matButton (click)="onBack()">Try Again</button>
        <button matButton mat-dialog-close>Close</button>
      }
    }
  </mat-dialog-actions>
